import type { CompanyLists } from "../../../server/core/lists";
import type { ScreenContext } from "../../shell/types";
import { api, el, toast, together } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, table } from "../../ui/record";
import { archiveModal, field, refuse } from "../clients/forms";
import { pageHead } from "../empty";

// Settings › Lists (brief 1E-25, mockup v86 settings/lists; DECISIONS 10/30): every list a screen
// picks from is the company's own, with starter items per module. Add, rename, archive (kept on
// old records) and restore. A screen that offers a list links here with ?list=<key>.
// Brief 1E-28: Payer fields, the fields each client type's patients carry (Admin), on its own tab.

type Lists = CompanyLists & { canEdit: boolean; error?: string };
type PayerField = {
  key?: string;
  label: string;
  kind: string;
  required: boolean;
  options?: string[];
  mask?: boolean;
};
type Types = {
  types: { id: string; name: string; archived: boolean; payerFields: PayerField[] }[];
  fieldKinds: { value: string; label: string }[];
  error?: string;
};
const PAYER = "payer_fields";
type List = Lists["lists"][number];
type Item = List["items"][number];

export async function listsScreen(host: HTMLElement, ctx: ScreenContext): Promise<void> {
  let data: Lists | null = null;
  let shown = new URLSearchParams(location.search).get("list") ?? "";
  const slot = el("div");
  host.replaceChildren(pageHead(ctx, "The choices each screen offers."), slot);

  let types: Types | null = null;
  async function reload() {
    // One call for the screen (brief 1E-29).
    const [res, ts] = await together(() =>
      Promise.all([
        api<Lists>("GET", "/api/settings/lists"),
        api<Types>("GET", "/api/settings/client-types"),
      ]),
    );
    if (!res.ok) return toast("Lists didn't load", res.body.error);
    data = res.body;
    // Payer fields are set on the client types, by an Admin.
    types = ts.ok ? ts.body : null;
    draw();
  }

  /** The pencil on a client type: the fields its patients carry. */
  function fieldsModal(t: Types["types"][number]) {
    const kinds = types?.fieldKinds ?? [];
    let fields: PayerField[] = structuredClone(t.payerFields).map(
      ({ key, label, kind, required, options, mask }) => ({
        key,
        label,
        kind,
        required,
        options,
        mask,
      }),
    );
    const rows = el("div", { "aria-label": "Details" });
    const drawRows = () =>
      rows.replaceChildren(
        ...fields.map((f, i) => {
          const label = el("input", {
            type: "text",
            value: f.label,
            maxlength: "60",
            "aria-label": `Field ${i + 1} name`,
          });
          label.addEventListener("input", () => (f.label = label.value));
          const kind = el(
            "select",
            { "aria-label": `Field ${i + 1} kind` },
            ...kinds.map((k) =>
              el("option", { value: k.value, selected: k.value === f.kind }, k.label),
            ),
          );
          kind.addEventListener("change", () => {
            f.kind = kind.value;
            drawRows();
          });
          const req = el("input", { type: "checkbox", checked: f.required });
          req.addEventListener("change", () => (f.required = req.checked));
          const opts =
            f.kind === "choice"
              ? (() => {
                  const o = el("input", {
                    type: "text",
                    value: (f.options ?? []).join(", "),
                    placeholder: "Options, separated by commas",
                    "aria-label": `${f.label || "Detail"} options`,
                  });
                  o.addEventListener(
                    "input",
                    () =>
                      (f.options = o.value
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean)),
                  );
                  return o;
                })()
              : null;
          return el(
            "div",
            { class: "row", style: "gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:6px" },
            label,
            kind,
            el("label", { class: "tsr" }, req, " Required"),
            opts ?? "",
            ib("x", `Take off ${f.label || "this detail"}`, () => {
              fields = fields.filter((x) => x !== f);
              drawRows();
            }),
          );
        }),
      );
    drawRows();
    const more = el("button", { type: "button", class: "btn sm" }, icon("plus"), " Add a detail");
    more.addEventListener("click", () => {
      fields.push({ label: "", kind: "text", required: false });
      drawRows();
    });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: `Payer details for ${t.name}`,
      sub: "Each patient of this client type shows them on Patient and payer.",
      wide: true,
      body: [el("div", { class: "form", style: "max-width:none" }, rows, more)],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api<unknown>("PUT", `/api/settings/client-types/${t.id}`, {
        payerFields: fields.map((f) => ({ ...f, key: f.key || undefined })),
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, (res.body as { error?: string }).error);
      m.close();
      toast("Payer details saved", "Shows on each patient of this client type");
      await reload();
    });
  }

  function payerTab(): HTMLElement {
    const live = (types?.types ?? []).filter((t) => !t.archived);
    return el(
      "section",
      { class: "card", "aria-label": "Payer details" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, "Payer details"),
          el("span", { class: "small muted" }, "Shown on Patient and payer"),
        ),
      ),
      table<Types["types"][number]>(
        [
          { h: "Client type", f: (t) => el("b", {}, t.name) },
          {
            h: "Details",
            f: (t) =>
              t.payerFields.length
                ? el("span", {}, ...t.payerFields.map((f) => chip(f.label, "plain")))
                : el("span", { class: "muted" }, "None"),
          },
          { h: "", f: (t) => ib("pencil", `Change ${t.name}`, () => fieldsModal(t)) },
        ],
        live,
        undefined,
        "Payer details",
      ),
    );
  }

  const path = (l: List, rest = "") =>
    `/api/settings/lists/${encodeURIComponent(l.key)}/items${rest}`;

  function nameModal(l: List, item: Item | null) {
    const name = field(item ? "Name" : "New item", item?.name ?? "", { maxlength: "80" });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: item ? `Rename ${item.name}` : `Add to ${l.name}`,
      body: [el("div", { class: "form", style: "max-width:none" }, name.el)],
      actions: [save],
    });
    name.input.focus();
    save.addEventListener("click", async () => {
      save.disabled = true;
      const res = await api<Lists>(item ? "PUT" : "POST", path(l, item ? `/${item.id}` : ""), {
        name: name.input.value,
      });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      data = res.body;
      draw();
      toast(item ? "Renamed" : "Added", name.input.value);
    });
  }

  function draw() {
    const d = data;
    if (!d) return;
    const onPayer = shown === PAYER && !!types;
    const list = d.lists.find((l) => l.key === shown) ?? d.lists[0];
    if (!list) return slot.replaceChildren(el("p", { class: "muted" }, "No lists yet."));
    if (!onPayer) shown = list.key;
    const tabOf = (key: string, name: string) => {
      const b = el(
        "button",
        { type: "button", role: "tab", "aria-selected": String(key === shown) },
        name,
      );
      b.addEventListener("click", () => {
        shown = key;
        history.replaceState(null, "", `${location.pathname}?list=${key}`);
        draw();
      });
      return b;
    };
    const tabs = el(
      "div",
      { class: "tabs", role: "tablist", style: "flex-wrap:wrap" },
      ...d.lists.map((l) => tabOf(l.key, l.name)),
      types ? tabOf(PAYER, "Payer details") : "",
    );
    if (onPayer) return slot.replaceChildren(tabs, payerTab());
    const add = d.canEdit
      ? (() => {
          const b = el("button", { type: "button", class: "btn sm" }, icon("plus"), " Add");
          b.addEventListener("click", () => nameModal(list, null));
          return b;
        })()
      : null;
    const restore = async (item: Item) => {
      const res = await api<Lists>("POST", path(list, `/${item.id}/restore`));
      if (!res.ok) return toast("Not restored", res.body.error);
      data = res.body;
      draw();
      toast("Restored", item.name);
    };
    slot.replaceChildren(
      tabs,
      el(
        "section",
        { class: "card", "aria-label": list.name },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, list.name),
            el("span", { class: "small muted" }, `Picked on ${list.usedOn}`),
          ),
          add,
        ),
        table<Item>(
          [
            {
              h: "Item",
              f: (r) =>
                el(
                  "span",
                  {},
                  el("b", {}, r.name),
                  r.archived ? " " : "",
                  r.archived ? chip("Archived", "plain") : "",
                ),
            },
            {
              h: "",
              f: (r) =>
                !d.canEdit
                  ? ""
                  : r.archived
                    ? ib("history", `Restore ${r.name}`, () => void restore(r))
                    : el(
                        "span",
                        { class: "rowbtns" },
                        ib("pencil", `Rename ${r.name}`, () => nameModal(list, r)),
                        ib("archive", `Archive ${r.name}`, () =>
                          archiveModal(r.name, path(list, `/${r.id}/archive`), () => reload()),
                        ),
                      ),
            },
          ],
          list.items,
          undefined,
          list.name,
        ),
      ),
    );
  }

  await reload();
}
